import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { post } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { ConfirmDialog } from '../components/ConfirmDialog';

interface FiveMConfig {
  fivem_jail_root:   string;
  fivem_txadmin_url: string;
  fivem_panel_port:  number;
  fivem_sftp_port:   number;
}

interface FiveMUser {
  discord_id: string;
  username:   string;
  permissions: string;
  created_at?: string;
}

export function FiveMConfigPage() {
  // Config state
  const [cfg,        setCfg]       = useState<FiveMConfig | null>(null);
  const [cfgLoading, setCfgLoading] = useState(false);
  const [saving,     setSaving]    = useState(false);
  const [jailRoot,   setJailRoot]  = useState('');
  const [txUrl,      setTxUrl]     = useState('');
  const [panelPort,  setPanelPort] = useState('');
  const [sftpPort,   setSftpPort]  = useState('');

  // Users state
  const [users,    setUsers]    = useState<FiveMUser[]>([]);
  const [uLoading, setULoading] = useState(false);

  // Create user form
  const [showCreate, setShowCreate] = useState(false);
  const [newDiscordId, setNewDiscordId] = useState('');
  const [newUsername,  setNewUsername]  = useState('');
  const [newPerms,     setNewPerms]     = useState('files');
  const [creating,     setCreating]     = useState(false);
  const [tempPw,       setTempPw]       = useState('');

  // Delete confirm
  const [confirmDel, setConfirmDel] = useState<FiveMUser | null>(null);
  const [deleting,   setDeleting]   = useState(false);

  const loadConfig = async () => {
    setCfgLoading(true);
    try {
      const data = await post<FiveMConfig>('/panel/backroom/fivem-config', { action: 'get' });
      setCfg(data);
      setJailRoot(data.fivem_jail_root  || '');
      setTxUrl(data.fivem_txadmin_url   || '');
      setPanelPort(String(data.fivem_panel_port || ''));
      setSftpPort(String(data.fivem_sftp_port   || ''));
    } catch (err: unknown) {
      toast.error('Failed to load FiveM config: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setCfgLoading(false);
    }
  };

  const loadUsers = async () => {
    setULoading(true);
    try {
      const data = await post<{ users: FiveMUser[] }>('/panel/backroom/fivem-users', { action: 'list' });
      setUsers(data.users || []);
    } catch (err: unknown) {
      toast.error('Failed to load FiveM users: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setULoading(false);
    }
  };

  useEffect(() => { loadConfig(); loadUsers(); }, []);

  const handleSaveConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!jailRoot.trim()) { toast.error('Jail root path is required.'); return; }
    setSaving(true);
    const id = toast.loading('Saving FiveM settings...');
    try {
      await post('/panel/backroom/fivem-config', {
        action:           'set',
        fivem_jail_root:  jailRoot.trim(),
        fivem_txadmin_url: txUrl.trim() || undefined,
        fivem_panel_port: panelPort ? parseInt(panelPort, 10) : undefined,
        fivem_sftp_port:  sftpPort  ? parseInt(sftpPort,  10) : undefined,
      });
      toast.success('FiveM settings saved', { id });
      await loadConfig();
    } catch (err: unknown) {
      toast.error('Failed: ' + (err instanceof Error ? err.message : 'Unknown error'), { id });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDiscordId.trim() || !newUsername.trim()) {
      toast.error('Discord ID and username are required.');
      return;
    }
    setCreating(true);
    const id = toast.loading(`Creating user ${newUsername}...`);
    try {
      const data = await post<{ ok: boolean; message: string; temp_password?: string }>(
        '/panel/backroom/fivem-users',
        { action: 'create', discord_id: newDiscordId.trim(), username: newUsername.trim(), permissions: newPerms }
      );
      toast.success(data.message || 'User created', { id });
      setTempPw(data.temp_password || '');
      setNewDiscordId(''); setNewUsername(''); setNewPerms('files');
      setShowCreate(false);
      await loadUsers();
    } catch (err: unknown) {
      toast.error('Failed: ' + (err instanceof Error ? err.message : 'Unknown error'), { id });
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    const id = toast.loading(`Removing ${confirmDel.username}...`);
    try {
      await post('/panel/backroom/fivem-users', { action: 'delete', discord_id: confirmDel.discord_id });
      toast.success(`${confirmDel.username} removed`, { id });
      await loadUsers();
    } catch (err: unknown) {
      toast.error('Failed: ' + (err instanceof Error ? err.message : 'Unknown error'), { id });
    } finally {
      setDeleting(false);
      setConfirmDel(null);
    }
  };

  return (
    <>
      {confirmDel && (
        <ConfirmDialog
          title="Remove FiveM User"
          message={
            <>
              Remove <span className="font-bold text-white">{confirmDel.username}</span> (
              <code className="font-mono text-cyberCyan">{confirmDel.discord_id}</code>) from FiveM panel access?
              Their account and sessions will be deleted.
            </>
          }
          confirmLabel="Remove User"
          confirmVariant="red"
          onConfirm={handleDeleteUser}
          onCancel={() => !deleting && setConfirmDel(null)}
        />
      )}

      <div className="flex flex-col gap-8 animate-fadeIn">
        {/* Header */}
        <div>
          <div className="text-[9px] font-mono tracking-[0.2em] text-slate-600 uppercase mb-1">Management</div>
          <h1 className="text-2xl font-black font-display uppercase tracking-tight text-white">
            FiveM <span className="bg-gradient-to-r from-cyberCyan to-blue-400 bg-clip-text text-transparent">Config</span>
          </h1>
        </div>

        {/* ── Section 1: Settings form ── */}
        <section>
          <h2 className="text-xs font-bold font-display uppercase tracking-wider text-slate-400 mb-4 flex items-center gap-2">
            <i className="fa-solid fa-gears text-cyberCyan/60" />
            Panel Settings
          </h2>
          <Card variant="glass" className="relative overflow-hidden p-6 max-w-2xl">
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-cyberCyan/10 to-transparent" />

            {cfgLoading && !cfg && (
              <p className="text-xs font-mono text-cyberCyan">
                <i className="fa-solid fa-spinner animate-spin mr-2" />Loading config...
              </p>
            )}

            {(cfg || !cfgLoading) && (
              <form onSubmit={handleSaveConfig} className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[9px] font-extrabold tracking-wider uppercase text-slate-400">
                    Jail Root Path <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={jailRoot}
                    onChange={e => setJailRoot(e.target.value)}
                    placeholder="e.g. C:\Users\Public\Desktop\FiveM\files"
                    className="input-base"
                    disabled={saving}
                  />
                  <p className="text-[9px] font-mono text-slate-600">SFTP file manager is jailed to this directory.</p>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-[9px] font-extrabold tracking-wider uppercase text-slate-400">txAdmin URL</label>
                  <input
                    type="text"
                    value={txUrl}
                    onChange={e => setTxUrl(e.target.value)}
                    placeholder="e.g. http://127.0.0.1:40120"
                    className="input-base"
                    disabled={saving}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[9px] font-extrabold tracking-wider uppercase text-slate-400">Panel Port</label>
                    <input
                      type="number"
                      value={panelPort}
                      onChange={e => setPanelPort(e.target.value)}
                      placeholder="8082"
                      min={1024} max={65535}
                      className="input-base"
                      disabled={saving}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[9px] font-extrabold tracking-wider uppercase text-slate-400">SFTP Port</label>
                    <input
                      type="number"
                      value={sftpPort}
                      onChange={e => setSftpPort(e.target.value)}
                      placeholder="2222"
                      min={1024} max={65535}
                      className="input-base"
                      disabled={saving}
                    />
                  </div>
                </div>

                <div className="flex justify-end mt-2">
                  <Button type="submit" variant="cyan" size="sm" loading={saving}>
                    <i className="fa-solid fa-floppy-disk" />
                    Save Settings
                  </Button>
                </div>
              </form>
            )}
          </Card>
        </section>

        {/* ── Section 2: FiveM Users ── */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xs font-bold font-display uppercase tracking-wider text-slate-400 flex items-center gap-2">
              <i className="fa-solid fa-users text-cyberCyan/60" />
              Panel Operators
            </h2>
            <Button variant="cyan" size="sm" onClick={() => { setShowCreate(!showCreate); setTempPw(''); }}>
              <i className="fa-solid fa-user-plus" />
              Create Operator
            </Button>
          </div>

          {/* Temp password banner */}
          {tempPw && (
            <Card variant="glass" className="p-5 mb-4 border-cyberGreen/25 bg-cyberGreen/5">
              <div className="flex items-center gap-2 text-cyberGreen font-bold text-xs uppercase tracking-wider mb-2">
                <i className="fa-solid fa-key" /> Temporary Credentials
              </div>
              <p className="text-xs text-slate-300 mb-3">
                DM sent to the user on Discord. Copy the temporary password if needed:
              </p>
              <code className="block bg-[#030508] p-3 rounded-lg border border-white/5 font-mono text-sm font-bold text-cyberCyan select-all w-fit">
                {tempPw}
              </code>
            </Card>
          )}

          {/* Create form */}
          {showCreate && (
            <Card variant="glass" className="p-6 mb-4 max-w-lg">
              <form onSubmit={handleCreateUser} className="flex flex-col gap-4">
                <h3 className="text-xs font-bold font-display uppercase tracking-wider text-white">New FiveM Operator</h3>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[9px] font-extrabold tracking-wider uppercase text-slate-400">Discord User ID</label>
                  <input type="text" value={newDiscordId} onChange={e => setNewDiscordId(e.target.value)}
                    placeholder="Numbers only (e.g. 172913386...)" className="input-base" disabled={creating} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[9px] font-extrabold tracking-wider uppercase text-slate-400">Display Name</label>
                  <input type="text" value={newUsername} onChange={e => setNewUsername(e.target.value)}
                    placeholder="Operator name" className="input-base" disabled={creating} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[9px] font-extrabold tracking-wider uppercase text-slate-400">Permissions</label>
                  <select value={newPerms} onChange={e => setNewPerms(e.target.value)} className="input-base" disabled={creating}>
                    <option value="files"        className="bg-[#080a0f]">File Explorer Only</option>
                    <option value="files,server" className="bg-[#080a0f]">File Explorer + Server Controls</option>
                    <option value="admin"        className="bg-[#080a0f]">Full Administrator</option>
                  </select>
                </div>
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setShowCreate(false)}>Cancel</Button>
                  <Button type="submit" variant="cyan" size="sm" loading={creating}>Create</Button>
                </div>
              </form>
            </Card>
          )}

          {/* Users table */}
          <Card variant="glass" className="overflow-hidden relative">
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-cyberCyan/10 to-transparent" />
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs font-mono">
                <thead className="bg-white/[0.02] text-slate-400 uppercase tracking-wider border-b border-white/[0.04]">
                  <tr>
                    <th className="px-5 py-3.5">Discord ID</th>
                    <th className="px-5 py-3.5">Username</th>
                    <th className="px-5 py-3.5">Permissions</th>
                    <th className="px-5 py-3.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.04] text-slate-200">
                  {uLoading && users.length === 0 && (
                    <tr><td colSpan={4} className="px-5 py-8 text-center text-cyberCyan">
                      <i className="fa-solid fa-spinner animate-spin mr-2" />Loading...
                    </td></tr>
                  )}
                  {!uLoading && users.length === 0 && (
                    <tr><td colSpan={4} className="px-5 py-8 text-center text-slate-500">
                      No operators configured.
                    </td></tr>
                  )}
                  {users.map(u => (
                    <tr key={u.discord_id} className="hover:bg-white/[0.01] transition-colors">
                      <td className="px-5 py-4 text-slate-400 font-bold">{u.discord_id}</td>
                      <td className="px-5 py-4 text-slate-200 font-semibold">{u.username}</td>
                      <td className="px-5 py-4">
                        <span className={`px-2 py-0.5 rounded text-[10px] ${
                          u.permissions === 'admin'
                            ? 'text-cyberCyan bg-cyberCyan/10 border border-cyberCyan/20'
                            : 'text-slate-400 bg-white/5 border border-white/10'
                        }`}>{u.permissions}</span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <button
                          onClick={() => setConfirmDel(u)}
                          className="text-slate-500 hover:text-red-400 transition-colors p-1 rounded"
                          title="Remove operator"
                          aria-label={`Remove ${u.username}`}
                        >
                          <i className="fa-solid fa-trash-can" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </section>
      </div>
    </>
  );
}
